import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronUp, Download } from "lucide-react";
import { toast } from "react-toastify";
import { returnVendorAPI } from "../../../services";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import "../../../style/returnsRegister.css";
import PlaneIcon from "../../../assets/icons/PlaneIcon";
import SearchIcon from "../../../assets/search-icon.svg";
import EditIcon from "../../../assets/icons/EditIcon";
import NoDataIcon from "../../../assets/icons/NoDataIcon";
import RedDeleteIcon from "../../../assets/icons/RedDeleteIcon";
import PrintReturnRegister from "./components/PrintReturnRegister";
import DeleteModal from "./components/DeleteModal";
import EditVendorReturnModal from "./components/EditVendorReturnModal";

import FilePdf from "../../../assets/file-pdf.svg";
import FileExcel from "../../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../../assets/file-csv.svg";
import { useSelector } from "react-redux";

/* ---------------- Tooltip Component ---------------- */
const Tooltip = ({ children, content, show }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = triggerRect.bottom + 8;
      let left =
        triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

      // Adjust if tooltip goes off screen
      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = triggerRect.top - tooltipRect.height - 8;
      }

      setPosition({ top, left });
    }
  }, [isVisible]);

  if (!show) return children;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        style={{ cursor: "help" }}
      >
        {children}
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          className="returns-tooltip"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            zIndex: 1000,
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};

function VendorReturnDetail() {
  const { isPartsURL } = useSelector((state) => state.domain);
  const navigate = useNavigate();
  const { id } = useParams();
  const [returns, setReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  // const [sortField, setSortField] = useState("");
  // const [sortDirection, setSortDirection] = useState("asc");
  const [balanceFilter, ] = useState("all");
  const [statusFilter, ] = useState("all");
  const [dateFilter, ] = useState("this_month");
  const [, setShowFilterDropdown] = useState(false);
  const [, setShowDateFilterDropdown] = useState(false);
  const [, setShowDeliveredReportDropdown] =
    useState(false);
  const [, setShowVendorReportDropdown] =
    useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [deliveredReportFilter, ] = useState("all");
  const [vendorReportFilter, ] = useState("all");
  const [loading, setLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [returnToDelete, setReturnToDelete] = useState(null);
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [returnToEdit, setReturnToEdit] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState({
    delivery_id: "",
    total_qty: 0,
    total_value: 0,
    vendor_name: "",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [, setTotalCount] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const filterDropdownRef = useRef(null);
  const dateFilterDropdownRef = useRef(null);
  const deliveredReportDropdownRef = useRef(null);
  const vendorReportDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const hasLoadedInitialData = useRef(false); // Prevent duplicate initial API calls
  const isFirstRender = useRef(true); // Track first render to prevent search effect on mount

  // Fetch Supplier return detail with filters and pagination
  const fetchReturns = async (
    search = "",
    page = 1,
    type = "",
    status = "all",
    dateFilter = "this_month",
    deliveredReportFilter = "all",
    vendorReportFilter = "all"
  ) => {
    if (!id) return;

    setLoading(true);
    try {
      // Map date filter value to human-readable label
      // Note: "this_month" is the default, so we send empty string to omit the parameter
      const dateFilterMap = {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        this_month: "", // Default - don't send parameter
        all: "",
      };
      const dateFilterLabel =
        dateFilterMap[dateFilter] !== undefined
          ? dateFilterMap[dateFilter]
          : dateFilter;

      // Map status filter value to human-readable label
      const statusFilterMap = {
        all: "",
        in_process: "In Process",
        under_repair: "Under Repair",
        not_repairable: "Not Repairable",
        completed: "Completed",
        successfully_returned: "Successfully Returned",
        refund: "Refund",
      };
      const statusFilterLabel = statusFilterMap[status] || status;

      // Map delivered report filter value to human-readable label
      const deliveredReportMap = {
        all: "",
        pending: "Pending",
        completed: "Completed",
      };
      const deliveredReportLabel =
        deliveredReportMap[deliveredReportFilter] || deliveredReportFilter;

      // Map Supplier report filter value to human-readable label
      const vendorReportMap = {
        all: "",
        active: "Active",
        inactive: "Inactive",
      };
      const vendorReportLabel =
        vendorReportMap[vendorReportFilter] || vendorReportFilter;

      const { response, error } = await returnVendorAPI.getVendorReturnDetail(
        id,
        search,
        page,
        itemsPerPage,
        statusFilterLabel,
        dateFilterLabel,
        type,
        deliveredReportLabel,
        vendorReportLabel
      );

      if (error) {
        toast.error(error.message || "Failed to fetch returns");
        setReturns([]);
        return;
      }

      // Handle API response structure
      // API returns: { id, delivery_id, status, total_qty, total_value, transactions: [], pagination: {} }
      let returnsList = response?.transactions || [];

      // Ensure returnsList is always an array
      if (!Array.isArray(returnsList)) {
        returnsList = [];
      }

      // Get pagination info from nested pagination object
      const pagination = response?.pagination || {};
      const totalItems = pagination.count || returnsList.length;
      const calculatedPages = Math.ceil(totalItems / itemsPerPage);

      setReturns(returnsList);
      setTotalCount(totalItems);
      setTotalPages(calculatedPages);

      // Update delivery info from root level of response
      setDeliveryInfo({
        delivery_id: response.delivery_id || "",
        total_qty: response.total_qty || 0,
        total_value: response.total_value || 0,
        vendor_name: response.vendor_name || "",
      });
    } catch (error) {
      console.error("Error fetching returns:", error);
      toast.error("Failed to fetch returns");
      setReturns([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load - only once, even in StrictMode
  useEffect(() => {
    if (id && !hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchReturns(
        searchTerm,
        currentPage,
        type,
        statusFilter,
        dateFilter,
        deliveredReportFilter,
        vendorReportFilter
      );
    }
    
    // Reset isFirstRender on mount (this runs on every mount, including StrictMode remounts)
    isFirstRender.current = true;
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        dateFilterDropdownRef.current &&
        !dateFilterDropdownRef.current.contains(event.target)
      ) {
        setShowDateFilterDropdown(false);
      }
      if (
        deliveredReportDropdownRef.current &&
        !deliveredReportDropdownRef.current.contains(event.target)
      ) {
        setShowDeliveredReportDropdown(false);
      }
      if (
        vendorReportDropdownRef.current &&
        !vendorReportDropdownRef.current.contains(event.target)
      ) {
        setShowVendorReportDropdown(false);
      }
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle search with API call (skip initial mount to avoid duplicate call)
  useEffect(() => {
    if (!id) return;

    // Skip the first render to prevent duplicate API call on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const delayedSearch = setTimeout(() => {
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchReturns(
        searchTerm,
        1,
        type,
        statusFilter,
        dateFilter,
        deliveredReportFilter,
        vendorReportFilter
      );
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    balanceFilter,
    statusFilter,
    dateFilter,
    deliveredReportFilter,
    vendorReportFilter,
  ]);

  // Set filtered returns to match API results
  // API already handles filtering, so we just set the filtered returns
  useEffect(() => {
    // Ensure returns is always an array
    const safeReturns = Array.isArray(returns) ? returns : [];
    setFilteredReturns(safeReturns);
  }, [returns]);

  // const handleSort = (field) => {
  //   if (sortField === field) {
  //     setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  //   } else {
  //     setSortField(field);
  //     setSortDirection("asc");
  //   }
  // };

  // Use delivery info from API
  const totalQty = deliveryInfo.total_qty || 0;
  const totalValue = deliveryInfo.total_value || 0;
  const vendorName = deliveryInfo.vendor_name || "-";
  const deliveryId = deliveryInfo.delivery_id || "-";

  // Format numbers for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Pagination - API handles pagination, so we use the full filtered results
  // Ensure currentReturns is always an array
  const currentReturns = Array.isArray(filteredReturns) ? filteredReturns : [];

  // const SortableHeader = ({ field, children }) => (
  //   <th className="sortable-header" onClick={() => handleSort(field)}>
  //     {children}
  //     {sortField === field && (
  //       <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
  //     )}
  //   </th>
  // );

  // Truncate text for display with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (!text && text !== 0) return "-";
    // Convert to string to handle numbers, objects, etc.
    const textStr = String(text);
    if (
      textStr === "null" ||
      textStr === "undefined" ||
      textStr === "[object Object]"
    )
      return "-";
    if (textStr.length <= maxLength) return textStr;
    return `${textStr.substring(0, maxLength)}...`;
  };

  // Export Options
  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "xlsx", label: "XLSX", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  const getExportLabel = () => {
    return "Export";
  };

  // Helper function to format return item data for export
  const formatReturnItemForExport = (returnItem) => {
    const product = returnItem.product || {};
    const device =
      product.name ||
      returnItem.product_name ||
      returnItem.device ||
      returnItem.model ||
      "-";
    const imei =
      returnItem.device_imei || returnItem.imei || returnItem.serial_no || "-";
    const defectNote =
      returnItem.remarks ||
      returnItem.defect_note ||
      returnItem.defect ||
      returnItem.notes ||
      "-";
    const returnId = returnItem.return_id || returnItem.id || "-";
    const buyingPrice =
      returnItem.device_buying_price ||
      product.latest_buying_price ||
      returnItem.buying_price ||
      returnItem.price ||
      0;

    return {
      returnId,
      device,
      imei,
      defectNote,
      buyingPrice: parseFloat(buyingPrice) || 0,
    };
  };

  // Fetch all data for export by paginating through all pages
  const fetchAllDataForExport = async () => {
    if (!id) return [];

    try {
      const dateFilterMap = {
        today: "Today",
        yesterday: "Yesterday",
        last_7_days: "Last 7 Days",
        this_month: "",
        all: "",
      };
      const dateFilterLabel = dateFilterMap[dateFilter] !== undefined ? dateFilterMap[dateFilter] : dateFilter;

      const statusFilterMap = {
        all: "",
        in_process: "In Process",
        under_repair: "Under Repair",
        not_repairable: "Not Repairable",
        completed: "Completed",
        successfully_returned: "Successfully Returned",
        refund: "Refund",
      };
      const statusFilterLabel = statusFilterMap[statusFilter] || statusFilter;

      const deliveredReportMap = {
        all: "",
        pending: "Pending",
        completed: "Completed",
      };
      const deliveredReportLabel = deliveredReportMap[deliveredReportFilter] || deliveredReportFilter;

      const vendorReportMap = {
        all: "",
        active: "Active",
        inactive: "Inactive",
      };
      const vendorReportLabel = vendorReportMap[vendorReportFilter] || vendorReportFilter;

      const type = balanceFilter === "positive" ? "credit" : balanceFilter === "negative" ? "debit" : "";

      let allData = [];
      // let currentPage = 1;
      // let hasMoreData = true;

      // Fetch first page to get total count
      const { response: firstResponse, error: firstError } = await returnVendorAPI.getVendorReturnDetail(
        id,
        searchTerm,
        1,
        itemsPerPage,
        statusFilterLabel,
        dateFilterLabel,
        type,
        deliveredReportLabel,
        vendorReportLabel
      );

      if (firstError) {
        toast.error(firstError.message || "Failed to fetch data for export");
        return [];
      }

      const firstPageData = Array.isArray(firstResponse?.transactions) ? firstResponse.transactions : [];
      allData = [...firstPageData];

      const totalCount = firstResponse?.pagination?.count || firstPageData.length;
      const totalPages = Math.ceil(totalCount / itemsPerPage);

      // Fetch remaining pages one by one
      for (let page = 2; page <= totalPages; page++) {
        const { response, error } = await returnVendorAPI.getVendorReturnDetail(
          id,
          searchTerm,
          page,
          itemsPerPage,
          statusFilterLabel,
          dateFilterLabel,
          type,
          deliveredReportLabel,
          vendorReportLabel
        );

        if (error) {
          console.error(`Error fetching page ${page}:`, error);
          break;
        }

        const pageData = Array.isArray(response?.transactions) ? response.transactions : [];
        allData = [...allData, ...pageData];
      }

      return allData;
    } catch (error) {
      console.error("Error fetching all data:", error);
      toast.error("Failed to fetch data for export");
      return [];
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    if (currentReturns.length === 0) {
      toast.error("No data to export");
      return;
    }

    toast.info("Preparing PDF export...");
    const allData = await fetchAllDataForExport();

    if (allData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add title - centered and bold
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    const titleText = "Supplier Return Detail Report";
    const titleWidth = doc.getTextWidth(titleText);
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(titleText, titleX, 22);
    doc.setFont(undefined, "normal");

    // Add delivery info on one line - centered
    doc.setFontSize(10);
    const deliveryInfoText = `Delivery ID: ${deliveryId} | Supplier: ${vendorName} | TOTAL QUANTITY: ${formatNumber(totalQty)} | TOTAL VALUE: ${formatCurrency(totalValue)}`;
    const deliveryInfoWidth = doc.getTextWidth(deliveryInfoText);
    const deliveryInfoX = (pageWidth - deliveryInfoWidth) / 2;
    doc.text(deliveryInfoText, deliveryInfoX, 30);

    // Add Generated on on a new line - centered
    const generatedOnText = `Generated on: ${new Date().toLocaleDateString()}`;
    const generatedOnWidth = doc.getTextWidth(generatedOnText);
    const generatedOnX = (pageWidth - generatedOnWidth) / 2;
    doc.text(generatedOnText, generatedOnX, 36);

    // Prepare table data
    const tableData = allData.map((returnItem) => {
      const formatted = formatReturnItemForExport(returnItem);
      return [
        formatted.returnId,
        formatted.device,
        formatted.imei,
        formatted.defectNote,
        `€${formatted.buyingPrice.toFixed(2)}`,
      ];
    });

    // Create table using autoTable
    autoTable(doc, {
      head: [
        [
          "Return ID",
          "Device",
          "IMEI / Serial No.",
          "Defect Note",
          "Buying Price",
        ],
      ],
      body: tableData,
      startY: 44,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [246, 248, 250],
        textColor: [87, 96, 106],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      columnStyles: {
        4: { halign: "left" }, // Buying Price column left-aligned
      },
    });

    // Save the PDF
    doc.save(`vendor-return-detail-${deliveryId}.pdf`);
    toast.success("PDF exported successfully");
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (currentReturns.length === 0) {
      toast.error("No data to export");
      return;
    }

    toast.info("Preparing Excel export...");
    const allData = await fetchAllDataForExport();

    if (allData.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Create summary header rows matching PDF format
    const summaryData = [
      { A: "Supplier Return Detail Report" },
      { A: `Delivery ID: ${deliveryId} | Supplier: ${vendorName} | TOTAL QUANTITY: ${formatNumber(totalQty)} | TOTAL VALUE: ${formatCurrency(totalValue)}` },
      { A: `Generated on: ${new Date().toLocaleDateString()}` },
      {}, // Empty row
    ];

    // Prepare data for Excel
    const excelData = allData.map((returnItem) => {
      const formatted = formatReturnItemForExport(returnItem);
      return {
        "Return ID": formatted.returnId,
        Device: formatted.device,
        "IMEI / Serial No.": formatted.imei,
        "Defect Note": formatted.defectNote,
        "Buying Price": formatted.buyingPrice,
      };
    });

    // Create worksheet with summary first
    const ws = XLSX.utils.json_to_sheet(summaryData, { skipHeader: true });
    XLSX.utils.sheet_add_json(ws, excelData, { origin: -1 });
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Returns");

    // Set column widths
    ws["!cols"] = [
      { wch: 15 }, // Return ID
      { wch: 25 }, // Device
      { wch: 20 }, // IMEI / Serial No.
      { wch: 30 }, // Defect Note
      { wch: 15 }, // Buying Price
    ];

    // Save the Excel file
    XLSX.writeFile(wb, `vendor-return-detail-${deliveryId}.xlsx`);
    toast.success("Excel file exported successfully");
  };

  // Export to CSV
  const exportToCSV = async () => {
    if (currentReturns.length === 0) {
      toast.error("No data to export");
      return;
    }

    toast.info("Preparing CSV export...");
    const allData = await fetchAllDataForExport();

    if (allData.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Create summary header rows matching PDF format
    const summaryRows = [
      ["Supplier Return Detail Report"],
      [`Delivery ID: ${deliveryId} | Supplier: ${vendorName} | TOTAL QUANTITY: ${formatNumber(totalQty)} | TOTAL VALUE: ${formatCurrency(totalValue)}`],
      [`Generated on: ${new Date().toLocaleDateString()}`],
      [], // Empty row
    ];

    // Prepare CSV data
    const csvHeaders = [
      "Return ID",
      "Device",
      "IMEI / Serial No.",
      "Defect Note",
      "Buying Price",
    ];
    const csvData = allData.map((returnItem) => {
      const formatted = formatReturnItemForExport(returnItem);
      return [
        formatted.returnId,
        formatted.device,
        formatted.imei,
        formatted.defectNote,
        formatted.buyingPrice,
      ];
    });

    // Combine summary, headers and data
    const csvContent = [...summaryRows, csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `vendor-return-detail-${deliveryId}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file exported successfully");
  };

  const handleExportSelect = (value) => {
    setShowExportDropdown(false);

    switch (value) {
      case "pdf":
        exportToPDF();
        break;
      case "xlsx":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      default:
        console.log(`Export format ${value} not supported`);
    }
  };

  const handleDeleteClick = (returnItem) => {
    setReturnToDelete(returnItem);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (returnItem) => {
    try {
      const transactionId = returnItem.id;
      
      if (!transactionId) {
        toast.error("Return ID is missing");
        setShowDeleteModal(false);
        setReturnToDelete(null);
        return;
      }

      // Call API to delete the Supplier return using return_id (e.g., "RTN-032")
      const { error } = await returnVendorAPI.deleteVendorReturn(transactionId);

      if (error) {
        toast.error(error.message || "Failed to delete return");
        return;
      }

      toast.success("Return deleted successfully");
      
      // Refresh the returns list
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchReturns(
        searchTerm,
        currentPage,
        type,
        statusFilter,
        dateFilter,
        deliveredReportFilter,
        vendorReportFilter
      );
    } catch (error) {
      console.error("Error deleting return:", error);
      toast.error("Failed to delete return");
    } finally {
      setShowDeleteModal(false);
      setReturnToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setReturnToDelete(null);
  };

  const handleEditClick = (returnItem) => {
    setReturnToEdit(returnItem);
    setShowEditModal(true);
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setReturnToEdit(null);
  };

  const handleEditSubmit = async (formData) => {
    try {
      const deviceId = returnToEdit?.return_device_id;
      
      if (!deviceId) {
        toast.error("Device ID is missing");
        return;
      }

      // Call API to update the device using return_device_id (e.g., "56")
      const { error } = await returnVendorAPI.updateDeviceById(
        deviceId,
        formData
      );

      if (error) {
        toast.error(error.message || "Failed to update return");
        return;
      }

      toast.success("Return updated successfully");
      setShowEditModal(false);
      setReturnToEdit(null);
      
      // Refresh the returns list
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      fetchReturns(
        searchTerm,
        currentPage,
        type,
        statusFilter,
        dateFilter,
        deliveredReportFilter,
        vendorReportFilter
      );
    } catch (error) {
      console.error("Error updating return:", error);
      toast.error("Failed to update return");
      throw error;
    }
  };

  // Handle status update to "Delivered"
  const handleDeliveredStatusUpdate = async () => {
    if (!id) {
      toast.error("Return ID is missing");
      return;
    }

    setUpdatingStatus(true);
    try {
      const { error } = await returnVendorAPI.updateVendorReturnStatus(
        id,
        "Delivered"
      );

      if (error) {
        toast.error(error.message || "Failed to update status");
        return;
      }

      toast.success("Status updated to Delivered successfully");

      // Navigate back to Supplier returns list
      setTimeout(() => {
        navigate("/returns/vendor");
      }, 1000); // Small delay to show the success message
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <>
      {/* back button */}
      <div style={{ marginBottom: "20px", marginTop: "-5px" }}>
        <button
          className={`${isPartsURL ? "back-button-parts" : "back-button"}`}
          onClick={() => navigate("/returns/vendor")}
        >
          <ArrowLeft size={16} /> Back to Record Lists
        </button>
      </div>

      {/* Content Area */}
      <div className="card rounded-3 p-4 border-0 mb-1 returns-container">
        <div className="returns-layout">
          {/* Header */}
          <div className="returns-top">
            <div className="returns-header">
              <div>
                <h2 className="returns-title">{deliveryId}</h2>
                <p className="returns-subtitle">
                  Check all delivery details and make a delivered
                </p>
              </div>

              <div className="d-flex gap-2 align-items-center">
                {/* Delivered */}
                <button
                  className={`${isPartsURL ? "add-returns-btn-parts" : "add-returns-btn"}`}
                  onClick={handleDeliveredStatusUpdate}
                  disabled={updatingStatus}
                  style={{
                    opacity: updatingStatus ? 0.6 : 1,
                    cursor: updatingStatus ? "not-allowed" : "pointer",
                  }}
                >
                  {updatingStatus ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        style={{ width: "16px", height: "16px" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <PlaneIcon fill="white"/> Delivered
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div
              className="row"
              style={{
                // height: "40px",
                // width: "550px",
                // backgroundColor: "#F6F8FA",
                // borderRadius: "6px",
                padding: "0px 0px 0px 10px ",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  color: "#57606A",
                  fontWeight: "400",
                  // padding: "0px",

                  height: "40px",
                  width: "550px",
                  backgroundColor: "#F6F8FA",
                  borderRadius: "6px",
                  padding: "10px 0px 0px 10px ",
                }}
              >
                TOTAL QUANTITY:{" "}
                <span style={{ fontWeight: "600" }}>
                  {formatNumber(totalQty)} |{" "}
                </span>
                TOTAL VALUE:{" "}
                <span style={{ fontWeight: "600" }}>
                  {formatCurrency(totalValue)} |{" "}
                </span>
                VENDOR: <span style={{ fontWeight: "600" }}>{vendorName}</span>
              </p>
            </div>

            {/* Search + Date Filter + Status Filter */}
            <div className="search-filter-section mb-4">
              {/* Search Bar */}
              <div className="returns-search-container">
                <input
                  type="text"
                  className={`${isPartsURL ? "returns-search-input-parts" : "returns-search-input"}`}
                  placeholder="Search by Return ID, Device, IMEI / Serial No..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="returns-search-icon"
                />
              </div>

              {/* Export Button */}
              <div className="returns-filter-dropdown" ref={exportDropdownRef}>
                <button
                  className={`${isPartsURL ? "returns-date-filter-btn-parts" : "returns-date-filter-btn"}`}
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                >
                  <Download size={16} />
                  {getExportLabel()}
                  {showExportDropdown ? (
                    <ChevronUp
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="returns-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`returns-filter-dropdown-content ${
                    showExportDropdown ? "show" : ""
                  }`}
                >
                  {exportOptions.map((option) => (
                    <div
                      key={option.value}
                      className="returns-filter-dropdown-item"
                      onClick={() => handleExportSelect(option.value)}
                    >
                      <img
                        src={option.icon}
                        alt={option.label}
                        width="16"
                        height="16"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Returns Table */}
          <div className="returns-table-wrapper">
            <div className="returns-table-responsive">
              <table className="returns-table">
                <thead>
                  <tr>
                    <th>RETURN ID</th>
                    <th>DEVICE</th>
                    <th>IMEI / SERIAL NO.</th>
                    <th>DEFECT NOTE</th>
                    <th>SELLING PRICE</th>
                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading returns...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : currentReturns.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>
                            No returns found
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentReturns.map((returnItem) => {
                      // Get product details from API response
                      const product = returnItem.product || {};
                      const device =
                        product.name ||
                        returnItem.product_name ||
                        returnItem.device ||
                        returnItem.model ||
                        "-";
                      const imei =
                        returnItem.device_imei ||
                        returnItem.imei ||
                        returnItem.serial_no ||
                        "-";
                      const defectNote =
                        returnItem.remarks ||
                        returnItem.defect_note ||
                        returnItem.defect ||
                        returnItem.notes ||
                        "-";
                      const returnId = returnItem.return_id || "-";
                      const buyingPrice =
                        returnItem.device_buying_price ||
                        product.latest_buying_price ||
                        returnItem.buying_price ||
                        returnItem.price ||
                        0;

                      // Build product details similar to RegisterReturns
                      const productDetails = [];
                      if (product.ram) productDetails.push(product.ram);
                      if (product.storage) productDetails.push(product.storage);
                      if (product.variants && product.variants.length > 0) {
                        productDetails.push(
                          product.variants
                            .map((variant) => Object.values(variant).join(", "))
                            .join(", ")
                        );
                      }
                      if (product.suffix) productDetails.push(product.suffix);
                      if (
                        product.used_mobile !== undefined &&
                        product.used_mobile !== null
                      ) {
                        productDetails.push(
                          product.used_mobile === false ? "New" : "Used"
                        );
                      }
                      const productDetailsText = productDetails.join(" | ");

                      const deviceTruncated = truncateText(device, 20);
                      const imeiTruncated = truncateText(imei, 15);
                      const defectNoteTruncated = truncateText(defectNote, 20);

                      const deviceString = String(device || "");
                      const imeiStr = String(imei || "");
                      const defectNoteStr = String(defectNote || "");

                      const isDeviceTruncated =
                        device !== "-" && deviceString.length > 20;
                      const showImeiTooltip =
                        imei !== "-" &&
                        imeiStr !== "-" &&
                        imeiStr !== "null" &&
                        imeiStr !== "undefined" &&
                        imeiStr.length > 15;
                      const showDefectNoteTooltip =
                        defectNote !== "-" &&
                        defectNoteStr !== "-" &&
                        defectNoteStr !== "null" &&
                        defectNoteStr !== "undefined" &&
                        defectNoteStr.length > 20;

                      // Create tooltip content with product name and details
                      const deviceTooltipContent = (
                        <div>
                          <div>{device}</div>
                          {productDetailsText && (
                            <div
                              style={{
                                fontSize: "0.8em",
                                marginTop: "4px",
                              }}
                            >
                              {productDetailsText}
                            </div>
                          )}
                        </div>
                      );

                      return (
                        <tr key={returnItem.return_id}>
                          <td>{returnId}</td>
                          <td>
                            {/* Device tooltip - same logic as RegisterReturns */}
                            {isDeviceTruncated ? (
                              // Show tooltip if device is truncated
                              <Tooltip
                                content={deviceTooltipContent}
                                show={isDeviceTruncated}
                              >
                                <span className="returns-table-cell-truncate">
                                  {deviceTruncated}
                                </span>
                              </Tooltip>
                            ) : productDetailsText ? (
                              // Show details directly in cell if device is not truncated
                              <div>
                                <div>{device}</div>
                                <div
                                  style={{
                                    fontSize: "0.8em",
                                    marginTop: "4px",
                                  }}
                                >
                                  {productDetailsText}
                                </div>
                              </div>
                            ) : (
                              // Just show device name if no details
                              <span className="returns-table-cell-truncate">
                                {deviceTruncated}
                              </span>
                            )}
                          </td>
                          <td>
                            <Tooltip content={imei} show={showImeiTooltip}>
                              <span className="returns-table-cell-truncate">
                                {imeiTruncated}
                              </span>
                            </Tooltip>
                          </td>
                          <td>
                            <Tooltip
                              content={defectNote}
                              show={showDefectNoteTooltip}
                            >
                              <span className="returns-table-cell-truncate">
                                {defectNoteTruncated}
                              </span>
                            </Tooltip>
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {formatCurrency(buyingPrice)}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className={`${isPartsURL ? "action-btn-parts view" : "action-btn view"}`}
                                onClick={() => handleEditClick(returnItem)}
                                title="Edit return"
                              >
                                <EditIcon fill={isPartsURL ? "var(--primary-color-parts)" : "var(--primary-color-mobile)"} />
                              </button>
                              <button
                                className="action-btn delete"
                                onClick={() => handleDeleteClick(returnItem)}
                                title="Delete return"
                              >
                                <RedDeleteIcon className="action-icon delete" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="returns-pagination-sticky">
              <div className="returns-pagination-container">
                <div className="returns-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="returns-pagination-buttons">
                  <button
                    className="previous"
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      const type =
                        balanceFilter === "positive"
                          ? "credit"
                          : balanceFilter === "negative"
                          ? "debit"
                          : "";
                      fetchReturns(
                        searchTerm,
                        newPage,
                        type,
                        statusFilter,
                        dateFilter,
                        deliveredReportFilter,
                        vendorReportFilter
                      );
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`${isPartsURL ? "next-returns-pagination-btn-parts" : "next-returns-pagination-btn"}`}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                      const type =
                        balanceFilter === "positive"
                          ? "credit"
                          : balanceFilter === "negative"
                          ? "debit"
                          : "";
                      fetchReturns(
                        searchTerm,
                        newPage,
                        type,
                        statusFilter,
                        dateFilter,
                        deliveredReportFilter,
                        vendorReportFilter
                      );
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Print Return Register Modal */}
      <PrintReturnRegister
        isOpen={showPrintModal}
        onClose={() => {
          setShowPrintModal(false);
          setSelectedReturn(null);
        }}
        returnData={selectedReturn}
        deliveryInfo={deliveryInfo}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        returnItem={returnToDelete}
      />

      {/* Add Manual Returns Modal */}
      <EditVendorReturnModal
        isOpen={showAddManualModal}
        onClose={() => setShowAddManualModal(false)}
        onSubmit={async (formData) => {
          try {
            toast.success("Return registered successfully");
            setShowAddManualModal(false);
            // Refresh the returns list
            const type =
              balanceFilter === "positive"
                ? "credit"
                : balanceFilter === "negative"
                ? "debit"
                : "";
            fetchReturns(
              searchTerm,
              currentPage,
              type,
              statusFilter,
              dateFilter,
              deliveredReportFilter,
              vendorReportFilter
            );
          } catch (error) {
            console.error("Error creating return:", error);
            toast.error("Failed to register return");
            throw error;
          }
        }}
      />

      {/* Edit Supplier Return Modal */}
      <EditVendorReturnModal
        isOpen={showEditModal}
        onClose={handleEditCancel}
        onSubmit={handleEditSubmit}
        repairId={returnToEdit?.return_device_id}
      />
    </>
  );
}

export default VendorReturnDetail;
